/**
 * ChangelogPage — user-facing "What's new". Reads published entries from the
 * public /api/changelog feed; authoring happens in the admin panel.
 */
import { useEffect, useState } from 'react'
import { api } from '../services/api.js'
import { usePageTitle } from '../hooks/usePageTitle.js'
import './ChangelogPage.css'

const TAG_LABEL = {
  new: 'New',
  improved: 'Improved',
  fixed: 'Fixed',
}

function formatDate(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
  } catch { return '' }
}

export default function ChangelogPage() {
  usePageTitle("What's new")
  const [entries, setEntries] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    api.get('/changelog')
      .then(setEntries)
      .catch(e => setError(e.message || 'Could not load the changelog'))
  }, [])

  return (
    <div className="changelog-page">
      <header className="changelog-page__header">
        <h1>What's new</h1>
        <p>Everything we've shipped to Genealogic, newest first.</p>
      </header>

      {error && <div className="changelog-page__error">{error}</div>}

      {entries === null && !error && (
        <div className="changelog-page__muted">Loading…</div>
      )}

      {entries && entries.length === 0 && (
        <div className="changelog-page__muted">No updates published yet — check back soon.</div>
      )}

      {entries && entries.length > 0 && (
        <ol className="changelog-page__list">
          {entries.map(e => (
            <li key={e.id} className="changelog-entry">
              <div className="changelog-entry__meta">
                <time className="changelog-entry__date">{formatDate(e.published_at)}</time>
                {e.tag && (
                  <span className={`changelog-entry__tag tag-${e.tag}`}>{TAG_LABEL[e.tag] || e.tag}</span>
                )}
              </div>
              <h2 className="changelog-entry__title">{e.title}</h2>
              {e.body && (
                <div className="changelog-entry__body">
                  {e.body.split('\n').filter(Boolean).map((line, i) => <p key={i}>{line}</p>)}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
